import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { formatNumber } from "../../../utils/format.ts";

export interface Figure {
  label: string;
  value: number | null;
  caption?: ReactNode;
  /** New in the last 24 hours, shown after the number. */
  added?: number | null;
}

export interface FigureSection {
  title: string;
  figures: Figure[];
}

/** Named groups of counts in a two-column grid, for a panel of many small figures. */
export default function FigureSections({ sections }: { sections: FigureSection[] }) {
  return (
    <Stack spacing={2}>
      {sections
        .filter((section) => section.figures.length > 0)
        .map((section) => (
          <Box key={section.title}>
            <Typography variant="overline" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
              {section.title}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 3, rowGap: 1.5 }}>
              {section.figures.map((f) => (
                <Box key={f.label} sx={{ minWidth: 0 }}>
                  <Stack direction="row" sx={{ alignItems: "baseline", gap: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1, color: "text.secondary" }} noWrap>
                      {f.label}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                      {f.value == null ? "–" : formatNumber(f.value)}
                    </Typography>
                    {!!f.added && (
                      <Typography
                        variant="caption"
                        sx={{ color: "success.main", fontVariantNumeric: "tabular-nums" }}
                      >
                        +{formatNumber(f.added)}
                      </Typography>
                    )}
                  </Stack>
                  {f.caption && (
                    <Typography variant="caption" component="div" sx={{ color: "text.secondary" }} noWrap>
                      {f.caption}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
    </Stack>
  );
}
